import { Link } from "react-router-dom";
import { ArrowRight, Target, Crosshair, ShieldCheck, Handshake } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { aboutImage, heroImage } from "@/lib/blogImages";

const valueIcons = [Crosshair, Target, ShieldCheck, Handshake];

export default function About() {
    const { t } = useLanguage();
    const a = t.aboutPage;

    return (
        <div data-testid="about-page" className="pt-36 sm:pt-44">
            {/* HEADER */}
            <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
                <Reveal>
                    <Eyebrow>{a.eyebrow}</Eyebrow>
                    <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.18] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {a.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{a.sub}</p>
                </Reveal>
            </section>

            {/* STORY */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                <div className="grid items-start gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>{a.storyEyebrow}</Eyebrow>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                {a.storyTitle}
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base">{a.storyP1}</p>
                            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{a.storyP2}</p>
                            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{a.storyP3}</p>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-10 grid gap-5 sm:grid-cols-2">
                            <div className="overflow-hidden rounded-2xl border border-slate-800">
                                <img
                                    src={aboutImage}
                                    alt="Không gian làm việc MAXTH"
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover"
                                />
                            </div>
                            <div className="hidden overflow-hidden rounded-2xl border border-slate-800 sm:block">
                                <img
                                    src={heroImage}
                                    alt="Hạ tầng hệ thống MAXTH"
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>

                    {/* FACTS */}
                    <Reveal delay={0.1} className="lg:col-span-5">
                        <div className="glass rounded-2xl p-7 sm:p-8">
                            <Eyebrow>{a.factsEyebrow}</Eyebrow>
                            <h2 className="font-display text-xl font-bold text-white sm:text-2xl">{a.factsTitle}</h2>
                            <dl className="mt-7 space-y-5">
                                {a.facts.map((f, i) => (
                                    <div
                                        key={i}
                                        className="border-b border-slate-800/80 pb-4 last:border-0 last:pb-0"
                                        data-testid={`fact-${i}`}
                                    >
                                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            {f.label}
                                        </dt>
                                        <dd className="mt-1.5 text-sm font-medium text-slate-200">{f.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* VALUES */}
            <section className="border-t border-slate-800/60 bg-ink2/30">
                <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                    <Reveal>
                        <Eyebrow>{a.valuesEyebrow}</Eyebrow>
                        <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                            {a.valuesTitle}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {a.values.map((v, i) => {
                            const Icon = valueIcons[i];
                            return (
                                <Reveal key={i} delay={0.07 * i} className={i % 2 === 1 ? "lg:translate-y-8" : ""}>
                                    <div className="glass card-lift h-full rounded-2xl p-7" data-testid={`value-card-${i}`}>
                                        <span className="font-mono text-xs text-neon">0{i + 1}</span>
                                        <Icon className="mt-5 h-6 w-6 text-neon" />
                                        <h3 className="mt-5 font-display text-lg font-bold text-white">{v.title}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* MISSION */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
                <Reveal>
                    <div className="glass relative overflow-hidden rounded-3xl px-8 py-14 sm:px-14">
                        <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-cobalt/15 blur-[100px]" aria-hidden="true" />
                        <div className="relative grid items-center gap-8 lg:grid-cols-12">
                            <div className="lg:col-span-8">
                                <Eyebrow>{a.missionTitle}</Eyebrow>
                                <p className="font-display text-xl font-bold leading-snug tracking-tight text-white sm:text-2xl lg:text-3xl">
                                    {a.missionText}
                                </p>
                            </div>
                            <div className="lg:col-span-4 lg:text-right">
                                <Link
                                    to="/lien-he"
                                    data-testid="about-cta-button"
                                    className="group inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-white"
                                >
                                    {t.nav.cta}
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
